import React, { useEffect, useState, useCallback } from 'react';
import { api, apiGet, Extraction, PaginatedResult } from '../api/client';
import EmailList from '../components/EmailList';
import EmailDetailComp from '../components/EmailDetail';
import AiAnalysisPanel from '../components/AiAnalysisPanel';

// ─── Types ────────────────────────────────────────────────────────────────────
interface MailboxInfo {
  id: string; name: string; userEmail: string;
  enabled: boolean; notificationMode: string;
  lastRunAt: string | null; subscription: { expiresAt: string } | null;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconSearch   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
const IconRefresh  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>;
const IconChevL    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m15 18-6-6 6-6"/></svg>;
const IconChevR    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m9 18 6-6-6-6"/></svg>;
const IconFilter   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>;
const IconMail     = () => <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C8C6C4" strokeWidth="1.25"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;

// ─── Avatar color (same as EmailList) ─────────────────────────────────────────
function avatarColor(str: string): string {
  const colors = ['#0078D4','#107C10','#5C2D91','#8764B8','#C43E1C','#038387','#004E8C','#69797E'];
  let hash = 0;
  for (const c of str) hash = c.charCodeAt(0) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

// ─── Mailbox pill tab ─────────────────────────────────────────────────────────
interface TabProps { mailbox: MailboxInfo | null; active: boolean; onClick: () => void; }

const MailboxTab: React.FC<TabProps> = ({ mailbox, active, onClick }) => {
  const isAll = !mailbox;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '5px 10px', border: 'none',
        borderRadius: '4px',
        background: active ? 'var(--accent-light)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-2)',
        fontWeight: active ? 600 : 400,
        fontSize: '12.5px', cursor: 'pointer',
        whiteSpace: 'nowrap', flexShrink: 0,
        transition: 'background .1s, color .1s',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
      }}
    >
      {isAll ? (
        <span style={{ width: 18, height: 18, borderRadius: '50%', background: active ? 'var(--accent)' : '#C8C6C4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '8px', fontWeight: 700 }}>
          ✦
        </span>
      ) : (
        <span style={{ width: 18, height: 18, borderRadius: '50%', background: avatarColor(mailbox.userEmail), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '8px', fontWeight: 700 }}>
          {mailbox.name.slice(0, 2).toUpperCase()}
        </span>
      )}
      {isAll ? 'All' : mailbox.name}
    </button>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyDetail: React.FC = () => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100%', gap: '16px',
  }}>
    <IconMail />
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-2)', marginBottom: '4px' }}>
        No message selected
      </div>
      <div style={{ fontSize: '13px', color: 'var(--text-3)', maxWidth: 260, lineHeight: 1.6 }}>
        Select an item from the list to read it here
      </div>
    </div>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────
const Extractions: React.FC = () => {
  const [mailboxes, setMailboxes]         = useState<MailboxInfo[]>([]);
  const [activeMailbox, setActiveMailbox] = useState<MailboxInfo | null>(null);

  const [result, setResult]   = useState<PaginatedResult<Extraction> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');
  const [page, setPage]       = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selectedId, setSelectedId]                 = useState<string | null>(null);
  const [selectedExtraction, setSelectedExtraction] = useState<Extraction | null>(null);
  const [detailLoading, setDetailLoading]           = useState(false);
  const [detailTab, setDetailTab]                   = useState<'email' | 'ai'>('email');

  useEffect(() => {
    apiGet<MailboxInfo[]>('/api/mailboxes').then(setMailboxes).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getExtractions({
        page, pageSize: 25,
        search:       debouncedSearch || undefined,
        status:       status         || undefined,
        mailboxEmail: activeMailbox?.userEmail || undefined,
      });
      setResult(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, status, activeMailbox]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setPage(1); setSelectedId(null); setSelectedExtraction(null); }, [debouncedSearch, status, activeMailbox]);

  const handleSelect = async (id: string) => {
    setSelectedId(id);
    setDetailTab('email');
    setDetailLoading(true);
    setSelectedExtraction(null);

    // Optimistically mark as read in the list
    setResult((prev) => prev ? {
      ...prev,
      items: prev.items.map((e) => e.id === id ? { ...e, isRead: true } : e),
    } : prev);

    try {
      const [extraction] = await Promise.all([
        api.getExtraction(id),
        api.markAsRead(id).catch(() => {}),
      ]);
      setSelectedExtraction(extraction);
    } catch { /* handled below */ }
    finally { setDetailLoading(false); }
  };

  const handleMarkUnread = async (id: string) => {
    setResult((prev) => prev ? {
      ...prev,
      items: prev.items.map((e) => e.id === id ? { ...e, isRead: false } : e),
    } : prev);
    setSelectedExtraction((prev) => prev ? { ...prev, isRead: false } : prev);
    await api.markAsUnread(id).catch(() => {});
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>

      {/* ════════════════════════════════════════════════════════════════════
          LEFT PANE — message list
      ═══════════════════════════════════════════════════════════════════════ */}
      <div style={{
        width: 370, minWidth: 300, flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        background: 'var(--card)',
        borderRight: '1px solid var(--border)',
        height: '100vh', overflow: 'hidden',
      }}>

        {/* ── Pane header ── */}
        <div style={{
          padding: '0 14px',
          height: 48,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          background: 'var(--card)',
        }}>
          <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.01em' }}>
            {activeMailbox ? activeMailbox.name : 'Inbox'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {result && (
              <span style={{ fontSize: '11px', color: 'var(--text-3)', marginRight: 4 }}>
                {result.total.toLocaleString()}
              </span>
            )}
            <button
              className="btn-cmd"
              onClick={loadData}
              title="Refresh"
              style={{ padding: '4px 6px' }}
            >
              <IconRefresh />
            </button>
          </div>
        </div>

        {/* ── Mailbox tabs ── */}
        {mailboxes.length > 0 && (
          <div style={{
            display: 'flex', gap: '2px', padding: '6px 10px 0',
            overflowX: 'auto', flexShrink: 0,
            borderBottom: '1px solid var(--border)',
          }}>
            <MailboxTab mailbox={null} active={activeMailbox === null} onClick={() => setActiveMailbox(null)} />
            {mailboxes.map((mb) => (
              <MailboxTab key={mb.id} mailbox={mb} active={activeMailbox?.id === mb.id} onClick={() => setActiveMailbox(mb)} />
            ))}
          </div>
        )}

        {/* ── Search box ── */}
        <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div className="search-box" style={{ flex: 1 }}>
            <span className="search-box-icon"><IconSearch /></span>
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* ── Command / filter bar ── */}
        <div className="command-bar">
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 7, color: 'var(--text-3)', display: 'flex', pointerEvents: 'none' }}>
              <IconFilter />
            </span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                paddingLeft: 24, paddingRight: 10, height: 28,
                border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                fontSize: '12px', background: 'var(--card)', color: 'var(--text-2)',
                cursor: 'pointer', appearance: 'none' as const,
              }}
            >
              <option value="">All Status</option>
              <option value="RECEIVED">Received</option>
              <option value="TASK_CREATED">Task Created</option>
              <option value="FAILED">Failed</option>
              <option value="PROCESSING">Processing</option>
              <option value="PENDING">Pending</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
        </div>

        {/* ── Message list ── */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {error ? (
            <div style={{ margin: '16px', padding: '10px 14px', background: 'var(--danger-light)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '13px' }}>
              {error}
            </div>
          ) : (
            <EmailList
              extractions={result?.items ?? []}
              selectedId={selectedId}
              onSelect={handleSelect}
              loading={loading}
            />
          )}
        </div>

        {/* ── Pagination ── */}
        {result && result.totalPages > 1 && (
          <div style={{
            padding: '8px 12px',
            borderTop: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexShrink: 0,
            background: 'var(--bg-subtle)',
          }}>
            <button
              className="btn-cmd"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ opacity: page === 1 ? .4 : 1 }}
            >
              <IconChevL /> Prev
            </button>
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>
              {page} of {result.totalPages}
            </span>
            <button
              className="btn-cmd"
              onClick={() => setPage((p) => Math.min(result.totalPages, p + 1))}
              disabled={page === result.totalPages}
              style={{ opacity: page === result.totalPages ? .4 : 1 }}
            >
              Next <IconChevR />
            </button>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          RIGHT PANE — reading pane
      ═══════════════════════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {!selectedId ? (
          <EmptyDetail />

        ) : detailLoading ? (
          <div style={{ padding: '24px 28px', flex: 1, overflow: 'auto' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center' }}>
              <div className="skeleton" style={{ width: 42, height: 42, borderRadius: '50%' }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div className="skeleton" style={{ height: 14, width: '35%' }} />
                <div className="skeleton" style={{ height: 12, width: '55%' }} />
              </div>
            </div>
            <div className="skeleton" style={{ height: 22, width: '70%', marginBottom: 16 }} />
            <div className="skeleton" style={{ height: 300 }} />
          </div>

        ) : selectedExtraction ? (
          <>
            {/* Tab bar (top of reading pane) */}
            <div style={{
              height: 48,
              display: 'flex', alignItems: 'center',
              padding: '0 20px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--card)',
              gap: '2px',
              flexShrink: 0,
            }}>
              {/* Mark as unread button — only shown when email is read */}
              {selectedExtraction.isRead && (
                <button
                  onClick={() => handleMarkUnread(selectedExtraction.id)}
                  title="Mark as unread"
                  style={{
                    marginRight: '8px',
                    padding: '4px 10px',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    background: 'transparent',
                    fontSize: '12px',
                    color: 'var(--text-2)',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '5px',
                    transition: 'background .15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3" fill="currentColor"/></svg>
                  Mark unread
                </button>
              )}
              {(['email', 'ai'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setDetailTab(tab)}
                  style={{
                    padding: '0 16px',
                    height: 46,
                    border: 'none',
                    borderBottom: `2px solid ${detailTab === tab ? 'var(--accent)' : 'transparent'}`,
                    borderRadius: 0,
                    background: 'transparent',
                    fontSize: '13px',
                    fontWeight: detailTab === tab ? 600 : 400,
                    color: detailTab === tab ? 'var(--accent)' : 'var(--text-2)',
                    cursor: 'pointer',
                    transition: 'border-color .1s, color .1s',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  {tab === 'email' ? (
                    <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg> Email</>
                  ) : (
                    <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> AI Analysis</>
                  )}
                </button>
              ))}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)' }} className="anim-slideIn">
              <div style={{ padding: '20px 28px' }}>
                {detailTab === 'email'
                  ? <EmailDetailComp extraction={selectedExtraction} />
                  : <AiAnalysisPanel extractionId={selectedExtraction.id} />
                }
              </div>
            </div>
          </>

        ) : (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-3)' }}>
            Failed to load message. Please try again.
          </div>
        )}
      </div>
    </div>
  );
};

export default Extractions;
